// WEBAPP-3557: This file should check to see if that team has already been imported on the jobsite.

import { AccordionDetails, AccordionSummary } from "@mui/material";
import { Alert, Checkbox } from "antd";
import React, { useEffect } from "react";
import GlobalStoreContext from "../../../../context/GlobalStoreContext";
import PlotAPI from "../../../../plotAPI";
import IntegrationIcon from "../../integrationIcon";
import { IntegrationName } from "../../integrationName";

interface Props {
    onClose: () => void;
    vendorMode?: boolean;
}

interface Vendor {
    id: string;
    name: string;
    checked?: boolean;
    contacts_checked?: string[];
    contacts: Contact[];
    linked_plot_team_id: number | null;
}

interface Contact {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    mobile: string | null;
}

const AddIntegrationVendorsToProject: React.FC<Props> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const [integrationVendors, setIntegrationVendors] = React.useState<Vendor[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [submitting, setSubmitting] = React.useState(false);

    useEffect(() => {
        if (
            context.currentProjectInfo.integration_source_project_id &&
            context.currentProjectInfo.integration_import_members
        ) {
            getIntegrationVendors();
        }
    }, [context.currentProjectInfo]);

    const integrationVendorsFilter = (vendor: Vendor) => {
        const identifiedGcCompanyId = context.currentProjectInfo.integration_source_identified_gc_company_id;

        if (identifiedGcCompanyId == null) {
            return true;
        }

        if (vendor.id.toString() == identifiedGcCompanyId.toString()) {
            return false;
        }

        return true;
    };

    const getIntegrationVendors = () => {
        setLoading(true);
        PlotAPI.getIntegrationContactsByVendor(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token
        ).then((response) => {
            if (response) {
                const filteredVendors = (response as Vendor[]).filter(integrationVendorsFilter);
                setIntegrationVendors(filteredVendors);
            }
            setLoading(false);
        });
    };

    const shouldCreateVendorTeam = (vendor: Vendor) => {
        return vendor.checked || (vendor.contacts_checked && vendor.contacts_checked.length > 0);
    };

    const getSelectedContactCount = (vendor: Vendor) => {
        return vendor.contacts_checked?.length || 0;
    };

    const getTotalSelectedCount = () => {
        return integrationVendors.reduce((sum, vendor) => sum + getSelectedContactCount(vendor), 0);
    };

    const handleVendorToggle = (vendorIndex: number, checked: boolean) => {
        setIntegrationVendors((prev) => {
            const updated = [...prev];
            updated[vendorIndex] = {
                ...updated[vendorIndex],
                checked,
                contacts_checked: checked ? updated[vendorIndex].contacts.map((c) => c.id) : [],
            };
            return updated;
        });
    };

    const handleContactsChange = (vendorIndex: number, contactIds: string[]) => {
        setIntegrationVendors((prev) => {
            const updated = [...prev];
            const vendor = updated[vendorIndex];
            vendor.contacts_checked = contactIds;
            vendor.checked = contactIds.length === vendor.contacts.length;
            updated[vendorIndex] = vendor;
            return updated;
        });
    };

    const onIntegrationCreate = () => {
        const vendorsAndContactsToCreate = integrationVendors.filter(shouldCreateVendorTeam).map((vendor) => {
            const contactsSelected = vendor.contacts_checked || [];
            const filteredContacts = vendor.contacts.filter((contact) => contactsSelected.includes(contact.id));
            return {
                ...vendor,
                contacts: filteredContacts,
            };
        });

        setSubmitting(true);

        PlotAPI.importMembersToJobsite(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            vendorsAndContactsToCreate,
            props.vendorMode ? "vendor" : "member"
        )
            .then(() => {
                context.refreshTeamsByProject();
                props.onClose();
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    if (!context.currentProjectInfo.integration_source_project_id) {
        return (
            <div>
                <Alert
                    type="warning"
                    title="This project is not connected to an integration that allows importing members."
                />
            </div>
        );
    }

    if (!context.currentProjectInfo.integration_import_members) {
        return (
            <div>
                <Alert
                    type="warning"
                    title={
                        <div>
                            <div className="text-center font-bold">
                                This project does not have importing from integrations enabled.
                            </div>
                            <div className="text-center">
                                Ask an Admin or Lead to enable this feature in the jobsite integration settings
                            </div>
                        </div>
                    }
                />
            </div>
        );
    }

    if (loading) {
        return <div className="py-10 text-center text-sm text-gray-400">Loading...</div>;
    }

    return (
        <>
            <AccordionSummary>
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span>Add {props.vendorMode ? "Vendors" : "Members"} from</span>
                    <IntegrationIcon project={context.currentProjectInfo} activated={true} />
                    <IntegrationName project={context.currentProjectInfo} />
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Select the companies that you would like to be created, then select the contacts for each
                        company you would like to be invited to them.
                    </p>

                    {integrationVendors.length === 0 ? (
                        <div className="py-10 text-center text-sm text-gray-400">No vendors found.</div>
                    ) : (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            <div className="max-h-96 overflow-y-auto">
                                {integrationVendors.map((vendor, vendorIndex) => {
                                    const selectedContactCount = getSelectedContactCount(vendor);
                                    const hasIndeterminate =
                                        selectedContactCount > 0 && selectedContactCount < vendor.contacts.length;
                                    const isFullySelected = selectedContactCount === vendor.contacts.length;

                                    return (
                                        <div
                                            key={vendor.id || vendorIndex}
                                            className="border-b border-gray-100 dark:border-gray-700/50 last:border-b-0"
                                        >
                                            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/40 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors">
                                                <div className="flex items-center gap-2">
                                                    <Checkbox
                                                        disabled={vendor.linked_plot_team_id ? true : false}
                                                        checked={vendor.checked || isFullySelected}
                                                        indeterminate={hasIndeterminate}
                                                        onChange={(e) =>
                                                            handleVendorToggle(vendorIndex, e.target.checked)
                                                        }
                                                        style={{ padding: "2px" }}
                                                    />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">
                                                        {vendor.name}
                                                        {vendor.linked_plot_team_id && (
                                                            <span className="ml-2 text-xs text-red-600 dark:text-green-400">
                                                                Already Imported
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>

                                            {vendor.contacts.length > 0 && (
                                                <div className="bg-white dark:bg-gray-900/50 px-3 py-2 ml-4">
                                                    <Checkbox.Group
                                                        disabled={vendor.linked_plot_team_id ? true : false}
                                                        value={vendor.contacts_checked || []}
                                                        onChange={(values) =>
                                                            handleContactsChange(vendorIndex, values as string[])
                                                        }
                                                        className="flex flex-col gap-1 ml-4"
                                                    >
                                                        {vendor.contacts.map((contact) => (
                                                            <Checkbox
                                                                key={contact.id}
                                                                value={contact.id}
                                                                disabled={vendor.linked_plot_team_id ? true : false}
                                                            >
                                                                <div className="flex flex-row items-center gap-2">
                                                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                                                        {contact.first_name} {contact.last_name}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-500">
                                                                        (
                                                                        {contact.email ??
                                                                            contact.mobile ??
                                                                            "No contact info"}
                                                                        )
                                                                    </div>
                                                                </div>
                                                            </Checkbox>
                                                        ))}
                                                    </Checkbox.Group>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                            {getTotalSelectedCount() > 0
                                ? `${getTotalSelectedCount()} contact${getTotalSelectedCount() !== 1 ? "s" : ""} selected`
                                : "No contacts selected"}
                        </span>
                    </div>

                    <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-700">
                        <button
                            type="button"
                            className="plot-button primary-button button"
                            onClick={onIntegrationCreate}
                            disabled={submitting || getTotalSelectedCount() === 0}
                        >
                            {submitting ? "Creating..." : "Create"}
                        </button>
                    </div>
                </div>
            </AccordionDetails>
        </>
    );
};

export default AddIntegrationVendorsToProject;
